import { Button, MagellanPopup } from "components/atoms";
import { Form, Formik } from "formik";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PiBroom } from "react-icons/pi";
import Popup from "reactjs-popup";
import { PopupActions } from "reactjs-popup/dist/types";
import { BUTTON_VARIANT } from "types/components/button/button.interface";

type FetchQrResult = string | { qrCodeBase64: string } | null | undefined;

type TwoFactorMode = "qr+otp" | "otp-only";

interface PopupTwoFactorFormValues {
  code: string;
}

interface IPopupTwoFactorProps {
  trigger: JSX.Element;
  header: React.ReactNode;

  /**
   * "qr+otp"  -> pobieramy i pokazujemy QR przy otwarciu
   * "otp-only"-> nie ma QR, tylko pole na kod
   */
  mode?: TwoFactorMode;

  /**
   * W trybie "qr+otp" jest wymagane (musi zwrócić QR lub null).
   * W trybie "otp-only" jest ignorowane.
   */
  fetchQr?: () => Promise<FetchQrResult>;

  /**
   * Wysyła kod OTP. Dostaje też popupRef, żebyś mógł go zamknąć w onSuccess.
   */
  submitOtp: (code: string, popup: PopupActions | null) => Promise<any>;

  /**
   * Zawartość formularza, np. <FormInput.Text name="code" .../>
   */
  children: React.ReactNode;

  /**
   * Tekst na przycisku submit.
   * Domyślnie "button.confirm" jeśli istnieje, inaczej fallback do "button.add".
   */
  submitLabelKey?: string;

  /**
   * Czy po zamknięciu / ponownym otwarciu resetować stan (QR, błędy, formularz).
   * Domyślnie true.
   */
  resetOnOpen?: boolean;

  /**
   * Pokazuje prosty błąd w modalu (np. gdy OTP niepoprawny).
   
   */
  showInlineError?: boolean;
}

const PopupTwoFactor = ({
  trigger,
  header,
  mode = "otp-only",
  fetchQr,
  submitOtp,
  children,
  submitLabelKey,
  resetOnOpen = true,
  showInlineError = false,
}: IPopupTwoFactorProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<PopupActions>(null);

  const shouldUseQr = mode === "qr+otp";

  const [qrImg, setQrImg] = useState<string | null>(null);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  const [inlineError, setInlineError] = useState<string | null>(null);

  const normalizeQr = (res: FetchQrResult): string | null => {
    if (!res) return null;
    const base64 = typeof res === "string" ? res : res.qrCodeBase64 ?? "";
    if (!base64) return null;

    return base64.startsWith("data:")
      ? base64
      : `data:image/png;base64,${base64}`;
  };

  const resolvedSubmitLabel = useMemo(() => {
    const key = submitLabelKey ?? "button.confirm";
    const translated = t(key);

    if (translated === key) return t("button.add");
    return translated;
  }, [submitLabelKey, t]);

  const handleOpen = async () => {
    if (resetOnOpen) {
      setQrImg(null);
      setQrError(null);
      setInlineError(null);
    }

    if (!shouldUseQr) return;

    // "qr+otp" wymaga fetchQr
    if (!fetchQr) {
      setQrError("Missing fetchQr");
      return;
    }

    setIsQrLoading(true);
    setQrError(null);
    setQrImg(null);

    try {
      const res = await fetchQr();
      const normalized = normalizeQr(res);

      if (!normalized) {
        setQrError("QR Error");
        setQrImg(null);
      } else {
        setQrImg(normalized);
      }
    } catch (err) {
      console.error("QR fetch failed", err);
      setQrError("QR Error");
      setQrImg(null);
    } finally {
      setIsQrLoading(false);
    }
  };

  const isSubmitBlocked = shouldUseQr && isQrLoading;

  return (
    <Popup
      ref={popupRef}
      modal
      nested
      onOpen={handleOpen}
      trigger={trigger}
      onClose={() => {}}
    >
      <Formik<PopupTwoFactorFormValues>
        initialValues={{ code: "" }}
        onSubmit={async (values, helpers) => {
          try {
            setInlineError(null);
            await submitOtp(values.code, popupRef.current);
            helpers.resetForm();
          } catch (e: any) {
            console.error("OTP submit error", e);
          }
        }}
      >
        {({ resetForm }) => (
          <Form className="w-full h-full">
            <MagellanPopup>
              <MagellanPopup.Header className="justify-center w-80 mb-5">
                {header}
              </MagellanPopup.Header>

              {/* Sekcja QR tylko w trybie "qr+otp" */}
              {shouldUseQr && (
                <>
                  {isQrLoading ? (
                    <p className="text-center">{t("loading")}</p>
                  ) : qrImg ? (
                    <img
                      src={qrImg}
                      className="w-40 h-40 mx-auto mb-4 object-contain"
                      alt="QR code"
                    />
                  ) : (
                    <p className="text-center">
                      {qrError ?? t("error.generic")}
                    </p>
                  )}
                </>
              )}

              {children}

              <MagellanPopup.BottomButtons>
                <Button
                  isClear
                  onlyIcon
                  type="button"
                  onClick={() => {
                    setInlineError(null);
                    resetForm();
                  }}
                  title={t("button.clear")}
                >
                  <Button.Icon icon={<PiBroom />} />
                </Button>

                <Button
                  type="submit"
                  variant={BUTTON_VARIANT.submit}
                  disabled={isSubmitBlocked}
                >
                  <Button.Text text={resolvedSubmitLabel} />
                </Button>
              </MagellanPopup.BottomButtons>
            </MagellanPopup>
          </Form>
        )}
      </Formik>
    </Popup>
  );
};

export default PopupTwoFactor;
